/**
 * API Client Service
 * Replaces AWS Amplify GraphQL client with REST API calls
 * 
 * Benefits:
 * - Simpler debugging (REST vs GraphQL)
 * - Lower latency (HTTP API vs AppSync)
 * - No Amplify dependency for data operations
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

class ApiClient {
  constructor() {
    // Remove trailing slash from base URL to prevent double slashes
    this.baseUrl = API_BASE_URL.replace(/\/+$/, '');
    this.authToken = null;
    // ASS-60: injected by auth.js at boot. Resolves a fresh token per request
    // so the SDK's auto-refresh fires whenever the cached id_token is stale.
    this._tokenResolver = null;
    this._signOutHandler = null;
    this._refreshPromise = null;
    this._signingOut = false;
  }

  setAuthToken(token) {
    this.authToken = token;
    // ASS-60: a fresh token means the session is live; clear the signing-out
    // flag so a future lapse can trigger sign-out again.
    this._signingOut = false;
  }

  clearAuthToken() {
    this.authToken = null;
  }

  setTokenResolver(fn) {
    this._tokenResolver = fn;
  }

  setSignOutHandler(fn) {
    this._signOutHandler = fn;
  }

  /** ASS-60: resolve a fresh token, coalescing concurrent calls.
   *  Returns true if a valid token is now set; false if resolution failed.
   *  On failure, CLEARS this.authToken so the truthiness check in the 401
   *  branch becomes meaningful (a stale token left in place would always
   *  pass the check and retry with the same dead token). */
  async _resolveToken() {
    if (!this._tokenResolver) return false;
    // Stampede guard: if a refresh is already in flight, join it rather
    // than starting another. Do NOT null _refreshPromise to force a new
    // one — that defeats the guard (five concurrent 401s would each null
    // and create their own promise).
    if (this._refreshPromise) {
      await this._refreshPromise;
      return !!this.authToken;
    }
    this._refreshPromise = this._tokenResolver()
      .then((result) => {
        if (result?.tokens?.idToken) {
          this.authToken = result.tokens.idToken;
          this._signingOut = false; // session is live again
        } else {
          // getSession resolved null (no user, or session.isValid() false).
          this.authToken = null;
        }
      })
      .catch(() => {
        // Token resolution failed (refresh token revoked, network error, etc.).
        // Clear the token so the 401 branch's comparison detects failure.
        this.authToken = null;
      })
      .finally(() => { this._refreshPromise = null; });
    await this._refreshPromise;
    return !!this.authToken;
  }

  async request(method, path, body = null, options = {}) {
    // ASS-60: resolve a fresh token before every request. The SDK's
    // getSession() checks expiry locally and only hits Cognito when the
    // cached id_token is actually expired, so this is cheap on the happy path.
    await this._resolveToken();

    // Snapshot the token we're about to use. After a 401, we compare against
    // this to decide whether the refresh produced a genuinely different token.
    const tokenUsed = this.authToken;

    const url = `${this.baseUrl}${path}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }
    const config = { method, headers };
    if (body && method !== 'GET') {
      config.body = JSON.stringify(body);
    }

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        // ASS-60: 401 branch — one refresh attempt, one retry, then sign out.
        if (response.status === 401 && this._tokenResolver && !options._isRetry) {
          // Do NOT null _refreshPromise here — if a refresh is already in
          // flight from another concurrent 401, we join it instead of
          // starting a second one. That is the stampede guard.
          await this._resolveToken();

          // Retry ONLY if the refresh produced a genuinely different token.
          // If authToken is null (resolution failed) or unchanged (same
          // stale token), fall through to sign-out.
          if (this.authToken && this.authToken !== tokenUsed) {
            return this.request(method, path, body, { ...options, _isRetry: true });
          }

          // Irrecoverable: refresh token revoked, session dead, or the
          // refresh returned the same token that already 401'd.
          // Dispatch exactly one sign-out (guard against N concurrent
          // failures each calling the handler).
          // ASS-60 v3: use a flag, not handler destruction. App.jsx registers
          // the handler once in useEffect([dispatch]) and never re-registers.
          // Nulling it would make the second session lapse silent. The flag
          // is cleared in _resolveToken()'s success branch and in setAuthToken()
          // so a future lapse after re-login can still sign out.
          if (this._signOutHandler && !this._signingOut) {
            this._signingOut = true;
            this._signOutHandler();
          }
        }
        const errorData = await response.json().catch(() => ({}));
        throw new ApiError(
          errorData.message || `HTTP ${response.status}`,
          response.status,
          errorData
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw new ApiError(error.message, 0, { originalError: error });
    }
  }

  // Convenience methods
  get(path, options) {
    return this.request('GET', path, null, options);
  }

  post(path, body, options) {
    return this.request('POST', path, body, options);
  }

  put(path, body, options) {
    return this.request('PUT', path, body, options);
  }

  delete(path, options) {
    return this.request('DELETE', path, null, options);
  }

  patch(path, body, options) {
    return this.request('PATCH', path, body, options);
  }
}

class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

// Singleton instance
const apiClient = new ApiClient();

// =============================================================================
// API FUNCTIONS - Replace GraphQL queries/mutations
// =============================================================================

// Health Check
export const checkHealth = () => apiClient.get('/health');

// -----------------------------------------------------------------------------
// ASSETS
// -----------------------------------------------------------------------------

export const listAssets = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return apiClient.get(`/assets${query ? `?${query}` : ''}`);
};

export const getAsset = (id) => apiClient.get(`/assets/${id}`);

export const createAsset = (data) => apiClient.post('/assets', data);

export const updateAsset = (id, data) => apiClient.put(`/assets/${id}`, data);

export const deleteAsset = (id) => apiClient.delete(`/assets/${id}`);

export const getAssetsByCustomer = (customerId, params = {}) => {
  const query = new URLSearchParams(params).toString();
  return apiClient.get(`/customers/${customerId}/assets${query ? `?${query}` : ''}`);
};

export const getAssetsByProject = (projectId, params = {}) => {
  const query = new URLSearchParams(params).toString();
  return apiClient.get(`/projects/${projectId}/assets${query ? `?${query}` : ''}`);
};

export const getAssetHistory = (assetId, params = {}) => {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v != null)
  );
  const query = new URLSearchParams(clean).toString();
  return apiClient.get(`/assets/${assetId}/history${query ? `?${query}` : ''}`);
};

// -----------------------------------------------------------------------------
// PROJECTS
// -----------------------------------------------------------------------------

export const listProjects = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return apiClient.get(`/projects${query ? `?${query}` : ''}`);
};

export const getProject = (id) => apiClient.get(`/projects/${id}`);

export const createProject = (data) => apiClient.post('/projects', data);

export const updateProject = (id, data) => apiClient.put(`/projects/${id}`, data);

export const deleteProject = (id) => apiClient.delete(`/projects/${id}`);

export const getProjectsByCustomer = (customerId, params = {}) => {
  const query = new URLSearchParams(params).toString();
  return apiClient.get(`/customers/${customerId}/projects${query ? `?${query}` : ''}`);
};

export const getProjectAreas = (projectId) => 
  apiClient.get(`/projects/${projectId}/areas`);

export const createProjectArea = (projectId, data) => 
  apiClient.post(`/projects/${projectId}/areas`, data);

// -----------------------------------------------------------------------------
// CUSTOMERS
// -----------------------------------------------------------------------------

export const listCustomers = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return apiClient.get(`/customers${query ? `?${query}` : ''}`);
};

export const getCustomer = (id) => apiClient.get(`/customers/${id}`);

export const createCustomer = (data) => apiClient.post('/customers', data);

export const updateCustomer = (id, data) => apiClient.put(`/customers/${id}`, data);

export const deleteCustomer = (id) => apiClient.delete(`/customers/${id}`);

// -----------------------------------------------------------------------------
// USERS (Cognito User Management)
// -----------------------------------------------------------------------------

export const listCustomerUsers = (customerId) => 
  apiClient.get(`/customers/${customerId}/users`);

export const createCustomerUser = (customerId, data) => 
  apiClient.post(`/customers/${customerId}/users`, data);

export const updateCustomerUser = (customerId, userId, data) => 
  apiClient.put(`/customers/${customerId}/users/${userId}`, data);

export const deleteCustomerUser = (customerId, userId) => 
  apiClient.delete(`/customers/${customerId}/users/${userId}`);

// AST-103: caller PINs for the phone delivery line. generateCallerPin returns
// the PIN once -- show it to the admin, never store it.
export const listCallerPinStatus = (customerId) =>
  apiClient.get(`/customers/${customerId}/users/pins`);

export const generateCallerPin = (customerId, userId) =>
  apiClient.post(`/customers/${customerId}/users/${userId}/pin`, {});

export const removeCallerPin = (customerId, userId) =>
  apiClient.delete(`/customers/${customerId}/users/${userId}/pin`);

// -----------------------------------------------------------------------------
// ASSET REASSIGNMENT
// -----------------------------------------------------------------------------

export const reassignAssets = (data) => 
  apiClient.post('/assets/reassign', data);

// -----------------------------------------------------------------------------
// ASSET TYPES
// -----------------------------------------------------------------------------

export const listAssetTypes = () => apiClient.get('/asset-types');

export const getAssetType = (id) => apiClient.get(`/asset-types/${id}`);

export const createAssetType = (data) => apiClient.post('/asset-types', data);

export const updateAssetType = (id, data) => apiClient.put(`/asset-types/${id}`, data);

export const deleteAssetType = (id) => apiClient.delete(`/asset-types/${id}`);

// -----------------------------------------------------------------------------
// ASSET SUBTYPES
// -----------------------------------------------------------------------------

export const listAssetSubtypes = () => apiClient.get('/asset-subtypes');

export const getAssetSubtypesByType = (typeId) => 
  apiClient.get(`/asset-types/${typeId}/subtypes`);

export const createAssetSubtype = (data) => apiClient.post('/asset-subtypes', data);

export const updateAssetSubtype = (id, data) => apiClient.put(`/asset-subtypes/${id}`, data);

export const deleteAssetSubtype = (id) => apiClient.delete(`/asset-subtypes/${id}`);

// -----------------------------------------------------------------------------
// ASSET OWNERS
// -----------------------------------------------------------------------------

export const listAssetOwners = () => apiClient.get('/asset-owners');

export const createAssetOwner = (data) => apiClient.post('/asset-owners', data);

// -----------------------------------------------------------------------------
// AREAS (Overlay Types)
// -----------------------------------------------------------------------------

export const listAreas = () => apiClient.get('/areas');

export const createArea = (data) => apiClient.post('/areas', data);

export const updateArea = (id, data) => apiClient.put(`/areas/${id}`, data);

export const deleteArea = (id) => apiClient.delete(`/areas/${id}`);

// -----------------------------------------------------------------------------
// MOVEMENT REPORTS
// -----------------------------------------------------------------------------

export const listMovementReports = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return apiClient.get(`/movement-reports${query ? `?${query}` : ''}`);
};

export const getMovementReportsByCustomer = (customerId, params = {}) => {
  const query = new URLSearchParams(params).toString();
  return apiClient.get(`/customers/${customerId}/movement-reports${query ? `?${query}` : ''}`);
};

// -----------------------------------------------------------------------------
// SEARCH
// -----------------------------------------------------------------------------

export const search = (query, type = 'assets', options = {}) => {
  const { customerId = null, limit = 50, page = 1 } = options;
  return apiClient.post('/search', { query, type, customerId, limit, page });
};

// -----------------------------------------------------------------------------
// AREA EVENTS
// -----------------------------------------------------------------------------

export const getAssetAreaEvents = (assetId, params = {}) => {
  const query = new URLSearchParams(params).toString();
  return apiClient.get(`/assets/${assetId}/area-events${query ? `?${query}` : ''}`);
};

export const getProjectAreaEvents = (projectId, params = {}) => {
  const query = new URLSearchParams(params).toString();
  return apiClient.get(`/projects/${projectId}/area-events${query ? `?${query}` : ''}`);
};

// -----------------------------------------------------------------------------
// ALERTS
// -----------------------------------------------------------------------------

export const listAlerts = () => apiClient.get('/alerts');

export const listCustomerAlerts = (customerId, params = {}) => {
  const query = new URLSearchParams(params).toString();
  return apiClient.get(`/customers/${customerId}/alerts${query ? `?${query}` : ''}`);
};

// -----------------------------------------------------------------------------
// PROJECT CONFIG
// -----------------------------------------------------------------------------

export const getProjectConfig = (projectId) => apiClient.get(`/projects/${projectId}/config`);

export const updateProjectConfig = (projectId, data) => apiClient.put(`/projects/${projectId}/config`, data);

// -----------------------------------------------------------------------------
// ASSET INGEST (SIMMS)
// -----------------------------------------------------------------------------

export const ingestAssets = (assets) => apiClient.post('/assets/ingest', { assets });

// Export client for direct access if needed
export { apiClient, ApiError };
export default apiClient;
